/**
 * apps/web :: components/whatsnext :: PhysicalLicenceSection
 *
 * Section 1 of the post-pass journey (ROADS-431).
 *
 * Pulls real apply content from `data/per-state-licensing.ts` (CA + TX
 * shipped today; other states show a "coming soon" graceful fallback).
 * Shows the canonical office-finder link, fee, and foreign-license
 * reciprocity note when present so the learner knows the deltas
 * relative to their home country before walking into the field office.
 *
 * Status-agnostic per ROADS-436: no copy refers to immigration status.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  getPerStateLicensing,
  type LocalizedString,
} from "@/data/per-state-licensing";

interface PhysicalLicenceSectionProps {
  /** USPS state code (uppercase). Required for content lookup. */
  readonly stateCode: string;
  /** Resolved language ("en" | "es"). */
  readonly lang: "en" | "es";
}

function localized(s: LocalizedString, lang: "en" | "es"): string {
  return s[lang] ?? s.en;
}

export default function PhysicalLicenceSection({
  stateCode,
  lang,
}: PhysicalLicenceSectionProps) {
  const { t } = useTranslation();
  const content = getPerStateLicensing(stateCode);

  // ─ Coming-soon fallback ─────────────────────────────────────────
  if (!content) {
    return (
      <section
        data-testid="whatsnext-section-licence"
        data-state={stateCode}
        data-status="coming-soon"
        aria-labelledby="whatsnext-licence-heading"
        className="rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
      >
        <h2
          id="whatsnext-licence-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("whatsnext.licence.heading")}
        </h2>
        <p className="mt-sm text-neutral-700">
          {t("whatsnext.licence.comingSoon", { state: stateCode })}
        </p>
      </section>
    );
  }

  const stateName = localized(content.displayName, lang);

  return (
    <section
      data-testid="whatsnext-section-licence"
      data-state={stateCode}
      data-status="ready"
      aria-labelledby="whatsnext-licence-heading"
      className="rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
    >
      <h2
        id="whatsnext-licence-heading"
        className="font-display text-xl font-bold text-brand-primary"
      >
        {t("whatsnext.licence.heading")}
      </h2>
      <p className="mt-sm text-neutral-700">
        {t("whatsnext.licence.intro", { state: stateName })}
      </p>

      {/* Apply link */}
      <div className="mt-md">
        <a
          href={content.apply.officeFinderUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("whatsnext.licence.findOffice", { state: stateName })}
          <span aria-hidden="true">→</span>
        </a>
      </div>

      {/* Fee */}
      <p className="mt-md text-sm text-neutral-700">
        {t("whatsnext.licence.fee", {
          amount: content.apply.fees.applicationUsd,
        })}
      </p>

      {/* Foreign-license reciprocity */}
      <div className="mt-md rounded-md border border-neutral-200 bg-slate-50 p-sm">
        <h3 className="font-semibold text-brand-primary">
          {t("whatsnext.licence.reciprocityHeading")}
        </h3>
        <p className="mt-xs text-sm text-neutral-800">
          {localized(content.apply.foreignLicenseReciprocity.summary, lang)}
        </p>
      </div>

      {/* Deep-link to the full apply page (already on main from ROADS-426). */}
      <p className="mt-md text-sm">
        <Link
          to={`/apply/${stateCode}`}
          className="font-semibold text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("whatsnext.licence.fullChecklistLink")} →
        </Link>
      </p>
    </section>
  );
}
